import { isSimpleStream, isLoadableStream } from './types';
import { selectors as streamSelectors } from '../../Stream';
import { reduxKey, initialState } from './constants';
import { getStreamItemId, isDeviation } from './helpers';
import { getIsMobile, getPagingMode } from '../../publicSession/selectors';
import { getCurrentModal } from '../../Modals/redux/selectors';
import { isAnyEditorFocused } from '../../DaEditorWrapper/redux/selectors';
import { getExtendedDeviationById, getDeviationById } from '../../redux/deviations/selectors';
import { createSelector } from 'reselect';
function getDuperbrowseState(state) {
    return state[reduxKey] || initialState;
}
function getTopStream(state) {
    const { streamStack, rootStream } = getDuperbrowseState(state);
    if (!streamStack.length) {
        return rootStream;
    }
    return streamStack[0];
}
function getExtendedItem(state, item, itemSchema) {
    if (item === undefined) {
        return;
    }
    switch(itemSchema){
        case 'deviation':
            if (isDeviation(item)) {
                var _getExtendedDeviationById;
                return (_getExtendedDeviationById = getExtendedDeviationById(state, item.deviationId)) !== null && _getExtendedDeviationById !== void 0 ? _getExtendedDeviationById : item;
            }
            if (typeof item === 'number' || typeof item === 'string') {
                return getExtendedDeviationById(state, item);
            }
            return;
        default:
            return item;
    }
}
function getSimpleItem(state, item, itemSchema) {
    if (item === undefined) {
        return;
    }
    switch(itemSchema){
        case 'deviation':
            if (isDeviation(item)) {
                return item;
            }
            if (typeof item === 'number' || typeof item === 'string') {
                return getDeviationById(state, item);
            }
            return;
        default:
            return item;
    }
}
export function getStreamItems(state, stream) {
    if (isSimpleStream(stream)) {
        return stream.items;
    }
    if (stream.streamItemSelector) {
        return stream.streamItemSelector(state, stream.streamId);
    }
    const streamItems = streamSelectors.getItems(state, stream.streamId);
    return streamItems;
}
export function getCurrentItem(state) {
    const topStream = getTopStream(state);
    if (!topStream) {
        return;
    }
    const items = getStreamItems(state, topStream);
    const item = items.find((i)=>getStreamItemId(i) === topStream.currentOpenItem);
    return getExtendedItem(state, item, topStream.itemSchema);
}
function getCurrentItemIndex(state) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return -1;
    }
    const items = getStreamItems(state, currentStream);
    return items.map(getStreamItemId).indexOf(currentStream.currentOpenItem);
}
function getStreamItemAtIndex(state, index) {
    const currentStream = getTopStream(state);
    if (!currentStream || index < 0) {
        return;
    }
    const items = getStreamItems(state, currentStream);
    return items[index];
}
function getNextItemNormalized(state) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    const index = getCurrentItemIndex(state);
    if (index < 0) {
        return;
    }
    return getStreamItemAtIndex(state, index + 1);
}
export function getNextItem(state) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    return getExtendedItem(state, getNextItemNormalized(state), currentStream.itemSchema);
}
function getPrevItemNormalized(state) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    const index = getCurrentItemIndex(state);
    if (index < 0) {
        return;
    }
    return getStreamItemAtIndex(state, index - 1);
}
export function getPrevItem(state) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    return getExtendedItem(state, getPrevItemNormalized(state), currentStream.itemSchema);
}
export function getIsDuperbrowseActive(state) {
    return getDuperbrowseState(state).isSessionActive;
}
export function getItemIdsForPreload(state, preloadDirection) {
    const nextItem = getNextItemNormalized(state);
    const prevItem = getPrevItemNormalized(state);
    const shouldPreloadDirection = (direction)=>!preloadDirection || preloadDirection === direction;
    const navigation = [
        nextItem && shouldPreloadDirection('next') && getStreamItemId(nextItem),
        prevItem && shouldPreloadDirection('prev') && getStreamItemId(prevItem)
    ].filter((item)=>!!item);
    return navigation;
}
export function getDuperbrowseNavigation(state) {
    return {
        nextItem: getNextItem(state),
        prevItem: getPrevItem(state)
    };
}
export function getDuperbrowseStreamParent(state) {
    const { streamStack: [currentStream] } = getDuperbrowseState(state);
    return currentStream && currentStream.parent;
}
export function getIsKeyboardDuperbrowseAllowed(state) {
    // say no to mobile
    if (getIsMobile(state)) {
        return false;
    }
    // modals get in the way, too
    if (getCurrentModal(state)) {
        return false;
    }
    if (isAnyEditorFocused(state)) {
        return false;
    }
    return true;
}
export function getIsLoading(state) {
    const currentStreamId = getCurrentStreamId(state);
    if (currentStreamId && streamSelectors.getIsValidStream(state, currentStreamId)) {
        return streamSelectors.isFetching(state, currentStreamId);
    }
    return false;
}
export function getLastViewedItem(state) {
    return getDuperbrowseState(state).lastViewedItem;
}
export const getCurrentBiMetadata = createSelector([
    getDuperbrowseState,
    (state, itemId)=>itemId
], (state, itemId)=>{
    var _state_currentBiMetadata;
    var _state_currentBiMetadata_itemId;
    return (_state_currentBiMetadata_itemId = state === null || state === void 0 ? void 0 : (_state_currentBiMetadata = state.currentBiMetadata) === null || _state_currentBiMetadata === void 0 ? void 0 : _state_currentBiMetadata[itemId]) !== null && _state_currentBiMetadata_itemId !== void 0 ? _state_currentBiMetadata_itemId : '';
});
export function getIsValidStreamItem(state, streamId, itemId) {
    const { streamStack, rootStream } = getDuperbrowseState(state);
    return [
        ...streamStack,
        rootStream
    ].some((stream)=>!!stream && stream.streamId === streamId && getStreamItems(state, stream).some((item)=>getStreamItemId(item) === itemId));
}
export function getCurrentStreamId(state) {
    const { streamStack: [topStream], rootStream } = getDuperbrowseState(state);
    if (topStream) {
        return topStream.streamId;
    }
    if (rootStream) {
        return rootStream.streamId;
    }
}
export function getPreloadingEnabled(state) {
    const { streamStack: [topStream], rootStream } = getDuperbrowseState(state);
    if (topStream) {
        var _topStream_preloadEnabled;
        return (_topStream_preloadEnabled = topStream.preloadEnabled) !== null && _topStream_preloadEnabled !== void 0 ? _topStream_preloadEnabled : true;
    }
    if (rootStream) {
        var _rootStream_preloadEnabled;
        return (_rootStream_preloadEnabled = rootStream.preloadEnabled) !== null && _rootStream_preloadEnabled !== void 0 ? _rootStream_preloadEnabled : true;
    }
    return true;
}
export function getRootLocation(state) {
    return getDuperbrowseState(state).rootLocation;
}
export function getCurrentStreamSchema(state) {
    const currentStream = getTopStream(state);
    return currentStream && currentStream.itemSchema;
}
export function getIsAnyStreamActive(state) {
    const duperState = getDuperbrowseState(state);
    return duperState.streamStack.length > 0 || !!duperState.rootStream;
}
export function getIsHistoryManagementEnabled(state) {
    var _getDuperbrowseState;
    var _getDuperbrowseState_isHistoryManagementEnabled;
    return (_getDuperbrowseState_isHistoryManagementEnabled = (_getDuperbrowseState = getDuperbrowseState(state)) === null || _getDuperbrowseState === void 0 ? void 0 : _getDuperbrowseState.isHistoryManagementEnabled) !== null && _getDuperbrowseState_isHistoryManagementEnabled !== void 0 ? _getDuperbrowseState_isHistoryManagementEnabled : true;
}
export function getNeedsToLoadMore(state, direction) {
    // we never load more in pagination mode
    if (getPagingMode(state) === 'page') {
        return false;
    }
    const currentStream = getTopStream(state);
    if (!currentStream || !isLoadableStream(currentStream) || getIsLoading(state)) {
        return false;
    }
    if (currentStream.enableLoader) {
        const items = getStreamItems(state, currentStream);
        const currentIndex = getCurrentItemIndex(state);
        const lookAheadSteps = 2;
        switch(direction){
            case 'next':
                return streamSelectors.hasMore(state, currentStream.streamId) && currentIndex + lookAheadSteps >= items.length;
            case 'prev':
                return streamSelectors.getHasLess(state, currentStream.streamId) && currentIndex - lookAheadSteps < 0;
            default:
                return false;
        }
    }
    return false;
}
export function getCurrentDuperbrowseContext(state, itemCount = 9) {
    const currentStream = getTopStream(state);
    if (!currentStream) {
        return;
    }
    const items = getStreamItems(state, currentStream);
    const currentIndex = getCurrentItemIndex(state);
    if (currentIndex < 0) {
        return;
    }
    const itemsToDuperbrowseItemList = (array)=>array.map((item)=>getSimpleItem(state, item, currentStream.itemSchema)).filter((item)=>!!item);
    if (items.length <= itemCount) {
        return itemsToDuperbrowseItemList(items);
    }
    // we want half the items before the current one, if possible
    const itemsToShowBefore = Math.floor((itemCount - 1) / 2);
    const startIndex = Math.max(currentIndex - itemsToShowBefore, 0);
    return itemsToDuperbrowseItemList(items.slice(startIndex, startIndex + itemCount));
}
export function getLoadCount(state) {
    var _getDuperbrowseState_loadCount;
    return (_getDuperbrowseState_loadCount = getDuperbrowseState(state).loadCount) !== null && _getDuperbrowseState_loadCount !== void 0 ? _getDuperbrowseState_loadCount : 0;
}
export function getRootStream(state) {
    return getDuperbrowseState(state).rootStream;
}

//# sourceMappingURL=selectors.js.map